import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { StatusPill, orderStatusTone } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { cn } from '../../lib/cn';
import { formatBusinessDate, businessDateParts } from '../../lib/dates';

interface DashboardData {
  me: {
    userId: string;
    today: { sales: string; orders: number };
    month: {
      netSales: string;
      orders: number;
      share: string;
      weightedNet: string;
      promoNet: string;
    };
    commission: {
      poolShare: string;
      online: string;
      crossSale: string;
      specialPayable: string;
      specialPending: string;
      total: string;
      shopCount: number;
    };
  };
  shop: {
    id: string;
    code: string | null;
    name: string | null;
    today: {
      sales: string;
      orders: number;
      cashExpected: string;
      deltaPct: number | null;
      pending: number;
    };
    month: {
      netSales: string;
      commissionPool: string;
      distributed: string;
      effectiveRatePct: string;
      qualified: boolean;
      qualifyingMin: string;
      currentTierIndex: number;
      nextThreshold: string | null;
      nextRatePct: string | null;
      amountToNext: string | null;
      tiers: Array<{ threshold: string; ratePct: string }>;
      teamSize: number;
    };
  };
  activeOrders: Array<{
    id: string;
    number: string;
    status: string;
    paymentStatus: string;
    total: string;
    balanceDue: string;
    createdAt: string;
    fulfillmentType: string;
    deliveryDate: string | null;
    customer: { id: string; name: string; companyName: string | null; type: string };
    lines: Array<{ qty: number; product: { name: string } }>;
  }>;
  upcomingDeliveries: Array<{
    id: string;
    number: string;
    deliveryDate: string;
    deliveryAddress: string | null;
    customer: { name: string; companyName: string | null; type: string };
    shop: { code: string };
  }>;
  lowStock: Array<{
    id: string;
    onHand: number;
    minThreshold: number;
    product: { id: string; sku: string; name: string };
  }>;
  alerts: Array<{
    kind: string;
    tone: 'warning' | 'negative' | 'info';
    title: string;
    body: string;
    at?: string;
  }>;
}

const greetings = ['Bonzour', 'Hello', 'Bonzour'];

export function SalesDashboard(): JSX.Element {
  const { user, perms } = useAuth();
  // The shop she's currently working from (nav switcher), falling back to her
  // home shop — so switching shops repoints the whole dashboard.
  const currentShopId = user?.activeShopId ?? user?.homeShopId ?? null;
  const data = useQuery({
    queryKey: ['sales-dashboard', user?.id, currentShopId],
    queryFn: () => apiGet<DashboardData>('/dashboards/sales/me'),
    enabled: !!currentShopId,
    refetchInterval: 60_000,
  });

  if (!user || !currentShopId) {
    // HQ-level user landed here — bounce them to the finance dashboard, which is
    // built for their persona. Gate on the SAME permission the /finance route
    // requires (finance.view_dashboard), NOT on role: a role-based bounce to a
    // permission-gated route ping-pongs "/" ↔ "/finance" forever for anyone
    // whose role is HQ-ish but who lacks the finance permission.
    if (perms.has('finance.view_dashboard')) {
      return <Navigate to="/finance" replace />;
    }
    return (
      <PageScaffold title="Home">
        <Card>
          <EmptyState
            title="You haven't been assigned to a shop yet"
            description="Ask an admin to set your home shop so this dashboard can populate."
          />
        </Card>
      </PageScaffold>
    );
  }
  if (data.isLoading || !data.data) {
    return (
      <PageScaffold title="Home">
        <div className="text-ink-3 text-center py-20">Loading your shop…</div>
      </PageScaffold>
    );
  }
  const d = data.data;
  // Prefer the shop the dashboard actually aggregated (the active shop),
  // falling back to whatever the session knows.
  const shopName = d.shop.name ?? user.activeShop?.name ?? user.homeShop?.name ?? 'Your shop';
  const greeting = greetings[new Date().getDay() % greetings.length] ?? 'Hello';
  const firstName = (user.name ?? '').split(' (')[0]?.split(' ')[0] ?? '';
  const today = new Date().toLocaleDateString('en-MU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <PageScaffold
      title={
        <>
          {greeting} {firstName} <span className="text-2xl">☀</span>
        </>
      }
      subtitle={
        <>
          {shopName} · {today}
        </>
      }
      actions={
        <>
          <Link to="/catalogue">
            <Button variant="secondary">Browse catalogue</Button>
          </Link>
          <Link to="/orders/new">
            <Button>+ New order</Button>
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — the work */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <TeamGoalCard
            month={d.shop.month}
            myMonth={d.me.month}
            myCommission={d.me.commission}
            shopName={shopName}
          />

          {/* Today at a glance — compact strip */}
          <Card bodyClassName="p-0">
            <div className="grid grid-cols-3 divide-x divide-rule-2">
              <Stat
                label="Shop sales today"
                value={<Money value={d.shop.today.sales} />}
                hint={
                  d.shop.today.deltaPct != null
                    ? `${d.shop.today.deltaPct > 0 ? '+' : ''}${d.shop.today.deltaPct.toFixed(0)}% vs 7-day avg`
                    : 'first sales of the week'
                }
                hintTone={
                  d.shop.today.deltaPct != null && d.shop.today.deltaPct > 0
                    ? 'positive'
                    : 'neutral'
                }
              />
              <Stat
                label="Orders today"
                value={<span className="kc-mono kc-tnum">{d.shop.today.orders}</span>}
                hint={
                  d.shop.today.pending > 0
                    ? `${d.shop.today.pending} still pending`
                    : 'all caught up'
                }
                hintTone={d.shop.today.pending > 0 ? 'warning' : 'neutral'}
              />
              <Stat
                label="Cash drawer (expected)"
                value={<Money value={d.shop.today.cashExpected} />}
                hint={
                  <Link to="/cash-close" className="text-accent hover:underline">
                    Cash close →
                  </Link>
                }
              />
            </div>
          </Card>

          {/* Active orders — scrollable card */}
          <Card
            title="Your active orders"
            subtitle="Resume a draft · follow up on confirmed sales"
            action={
              <Link to="/orders" className="text-xs text-accent hover:underline">
                All orders →
              </Link>
            }
            bodyClassName="p-0"
          >
            {d.activeOrders.length === 0 ? (
              <EmptyState
                title="No active orders"
                description="Create a new order to get started."
                action={
                  <Link to="/orders/new">
                    <Button size="sm">+ New order</Button>
                  </Link>
                }
              />
            ) : (
              <div className="max-h-[380px] overflow-y-auto overflow-x-hidden">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col className="w-[20%]" />
                    <col className="w-[22%]" />
                    <col className="w-[26%]" />
                    <col className="w-[14%]" />
                    <col className="w-[18%]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-paper-2">
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
                      <th className="px-4 py-2 font-medium">Invoice</th>
                      <th className="px-4 py-2 font-medium">Customer</th>
                      <th className="px-4 py-2 font-medium">Items</th>
                      <th className="px-4 py-2 font-medium text-right">Total</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.activeOrders.map((o) => {
                      const displayName =
                        o.customer.type === 'company'
                          ? (o.customer.companyName ?? o.customer.name)
                          : o.customer.name;
                      const itemSummary =
                        o.lines
                          .slice(0, 3)
                          .map((l) => `${l.qty}× ${l.product.name}`)
                          .join(', ') + (o.lines.length > 3 ? ', …' : '');
                      const next =
                        o.status === 'DRAFT'
                          ? null
                          : o.fulfillmentType === 'delivery' && o.deliveryDate
                            ? `Delivery ${formatBusinessDate(o.deliveryDate, { weekday: 'short', day: 'numeric', month: 'short' })}`
                            : o.fulfillmentType === 'pickup'
                              ? 'In-shop pickup'
                              : null;
                      return (
                        <tr
                          key={o.id}
                          className="border-b border-rule-2 hover:bg-paper-2 align-top"
                        >
                          <td className="px-4 py-3">
                            <Link
                              to={`/orders/${o.id}`}
                              className="kc-mono text-ink hover:text-accent hover:underline text-xs break-all"
                            >
                              {o.number.startsWith('DRAFT-') ? 'DRAFT' : o.number}
                            </Link>
                            <div className="text-xs text-ink-4 mt-0.5">
                              {new Date(o.createdAt).toLocaleTimeString('en-MU', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/customers/${o.customer.id}`}
                              className="text-ink hover:text-accent hover:underline block truncate"
                            >
                              {displayName}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-xs text-ink-3 truncate" title={itemSummary}>
                            {itemSummary}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Money value={o.total} />
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill tone={orderStatusTone(o.status)}>{o.status}</StatusPill>
                            {next && (
                              <div className="text-[10px] text-ink-3 mt-1 truncate">{next}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT — sidebar */}
        <div className="space-y-6">
          <Card title="Quick actions">
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: '/orders/new', label: '+ New order', icon: '🧾', primary: true },
                { to: '/catalogue', label: 'Catalogue', icon: '📚' },
                { to: '/customers', label: 'Customers', icon: '👥' },
                { to: '/cash-close', label: 'Cash close', icon: '💰' },
              ].map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className={cn(
                    'flex items-center gap-2 px-4 py-3 rounded text-sm font-medium border transition',
                    a.primary
                      ? 'bg-accent text-white border-accent hover:bg-accent-2'
                      : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40 hover:text-ink',
                  )}
                >
                  <span className="text-base">{a.icon}</span>
                  {a.label}
                </Link>
              ))}
            </div>
          </Card>

          <Card
            title="Coming up"
            subtitle="Deliveries from your sales"
            bodyClassName={d.upcomingDeliveries.length > 0 ? 'p-0' : undefined}
          >
            {d.upcomingDeliveries.length === 0 ? (
              <div className="text-sm text-ink-3 text-center py-4">No deliveries scheduled</div>
            ) : (
              <ul className="max-h-[260px] overflow-y-auto divide-y divide-rule-2">
                {d.upcomingDeliveries.map((o) => {
                  const dt = businessDateParts(o.deliveryDate);
                  const displayName =
                    o.customer.type === 'company'
                      ? (o.customer.companyName ?? o.customer.name)
                      : o.customer.name;
                  return (
                    <li key={o.id} className="flex items-start gap-3 px-6 py-3">
                      <div className="text-center w-11 shrink-0 border-r border-rule-2 pr-3">
                        <div className="kc-serif text-2xl leading-none text-ink">
                          {dt.day}
                        </div>
                        <div className="text-[10px] text-ink-3 uppercase tracking-wider mt-0.5">
                          {dt.weekday} {dt.month}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/orders/${o.id}`}
                          className="text-sm text-ink hover:text-accent block truncate"
                        >
                          {displayName}
                        </Link>
                        <div className="text-[11px] text-ink-3 truncate">
                          {o.deliveryAddress ?? `Shop ${o.shop.code}`}
                        </div>
                      </div>
                      <span className="text-ink-3 text-sm mt-0.5">🚚</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card
            title="Heads up"
            action={
              d.alerts.length > 0 ? <StatusPill tone="info">{d.alerts.length}</StatusPill> : null
            }
          >
            {d.alerts.length === 0 ? (
              <div className="text-sm text-ink-3 text-center py-2">Nothing urgent ✓</div>
            ) : (
              <ul className="space-y-3 max-h-[280px] overflow-y-auto">
                {d.alerts.map((a, i) => (
                  <li key={i} className="flex gap-2.5 text-sm">
                    <span
                      className={cn('mt-1.5 w-1.5 h-1.5 rounded-full shrink-0', {
                        'bg-negative': a.tone === 'negative',
                        'bg-warning': a.tone === 'warning',
                        'bg-info': a.tone === 'info',
                      })}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink truncate">{a.title}</div>
                      <div className="text-xs text-ink-3 mt-0.5">{a.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Team goal — the heart of the page. The shop climbs its barem on the SHOP's
// monthly net; the pool it unlocks is then shared PRO-RATA by what each girl
// sold (promo / discounted sales count half). Her estimated cut is shown here;
// the full breakdown (other shops, online, special models) lives on
// /my-commission.
// ─────────────────────────────────────────────────────────────────────────────
function fmtPct(rate: string): string {
  // Show the exact configured rate — never round (0.75% must stay 0.75%, not 0.8%).
  const p = Number(rate) * 100;
  return `${Number(p.toFixed(6))}%`;
}
function fmtK(v: string): string {
  const n = Number(v);
  return n >= 1000 ? `Rs ${(n / 1000).toFixed(0)}k` : `Rs ${n.toFixed(0)}`;
}

function TeamGoalCard({
  month,
  myMonth,
  myCommission,
  shopName,
}: {
  month: DashboardData['shop']['month'];
  myMonth: DashboardData['me']['month'];
  myCommission: DashboardData['me']['commission'];
  shopName: string;
}): JSX.Element {
  const net = Number(month.netSales);
  const tiers = month.tiers;
  const idx = month.currentTierIndex;
  const qualifyingMin = Number(month.qualifyingMin);
  // The team only actually EARNS once it's past the qualifying minimum and into a
  // tier. Crossing a Rs 0 threshold is not the same as earning — don't tick it.
  const earning = month.qualified && idx >= 0;
  const belowMin = !month.qualified && qualifyingMin > 0;
  const topReached = earning && idx === tiers.length - 1;
  const earningLabel = earning ? fmtPct(month.effectiveRatePct) : '0%';
  const teammates = month.teamSize > 0 ? month.teamSize : 1;

  // Progress bar: toward the qualifying minimum while below it, otherwise within
  // the current segment (current threshold → next threshold).
  const lower = earning ? Number(tiers[idx]!.threshold) : 0;
  const upper = belowMin
    ? qualifyingMin
    : month.nextThreshold != null
      ? Number(month.nextThreshold)
      : tiers.length > 0
        ? Number(tiers[tiers.length - 1]!.threshold)
        : 0;
  const segPct = topReached
    ? 100
    : upper > lower
      ? Math.min(100, Math.max(0, ((net - lower) / (upper - lower)) * 100))
      : 0;

  return (
    <Card bodyClassName="p-0" className="overflow-hidden">
      {/* Progress hero */}
      <div className="p-6 bg-accent-soft">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-accent font-semibold">
              {shopName} · team goal this month
            </div>
            <Money value={month.netSales} className="kc-serif text-4xl text-accent mt-1 block" />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-accent/70 font-semibold">
              Team earning now
            </div>
            <div className="text-2xl text-accent font-medium kc-tnum">{earningLabel}</div>
          </div>
        </div>

        {tiers.length > 0 ? (
          <>
            {/* progress to the next tier */}
            <div className="mt-4 h-2.5 bg-white/50 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  topReached ? 'bg-positive' : 'bg-accent',
                )}
                style={{ width: `${segPct}%` }}
              />
            </div>
            <div className="text-xs text-accent/90 mt-2">
              {topReached ? (
                <span className="font-medium">
                  🎉 Top tier reached — {earningLabel} on every sale, for the whole team!
                </span>
              ) : belowMin ? (
                <>
                  <Money
                    value={String(Math.max(0, qualifyingMin - net))}
                    className="text-accent/90"
                  />{' '}
                  more in team sales to{' '}
                  <span className="font-medium">start earning commission</span>
                </>
              ) : (
                <>
                  <Money value={month.amountToNext ?? '0'} className="text-accent/90" /> more to
                  {idx < 0 ? ' start earning ' : ' bump the whole team to '}
                  <span className="font-medium">{fmtPct(month.nextRatePct ?? '0')}</span>
                </>
              )}
            </div>

            {/* all tiers — which are unlocked */}
            <div className="flex items-stretch gap-1.5 mt-4">
              {tiers.map((t, i) => {
                const passed = earning && net >= Number(t.threshold);
                const current = earning && i === idx;
                return (
                  <div key={i} className="flex-1">
                    <div className={cn('h-1 rounded-full', passed ? 'bg-accent' : 'bg-white/60')} />
                    <div
                      className={cn(
                        'mt-1.5 text-[10px] flex items-center gap-1 flex-wrap',
                        current
                          ? 'text-accent font-semibold'
                          : passed
                            ? 'text-accent/80'
                            : 'text-ink-3',
                      )}
                    >
                      <span>{passed ? '✓' : '○'}</span>
                      <span className="kc-tnum">{fmtK(t.threshold)}</span>
                      <span className="text-ink-3">·</span>
                      <span>{fmtPct(t.ratePct)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-xs text-accent/80 mt-3">
            No commission tiers set yet — ask an admin to configure them.
          </div>
        )}
      </div>

      {/* Shared pool + her sales + her cut */}
      <div className="grid grid-cols-3 divide-x divide-rule-2 border-t border-rule-2">
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Team commission pool
          </div>
          <Money value={month.commissionPool} className="text-xl text-ink mt-1 block" />
          <div className="text-[11px] text-ink-3 mt-1">
            shared pro-rata by sales across {teammates} teammate{teammates === 1 ? '' : 's'}
            {earning ? '' : ' · unlocks once the shop qualifies'}
          </div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Your contribution
          </div>
          <Money value={myMonth.netSales} className="text-xl text-ink mt-1 block" />
          <div className="text-[11px] text-ink-3 mt-1">
            {myMonth.orders} order{myMonth.orders === 1 ? '' : 's'} this month
            {Number(myMonth.promoNet) > 0 && <span title="Promo / discounted sales earn half commission"> · promo counts ½</span>}
          </div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">Your est. share</div>
          <Money value={myMonth.share} className="text-xl text-accent font-medium mt-1 block" />
          <div className="text-[11px] text-ink-3 mt-1">
            pool × your sales ÷ shop sales 💪
          </div>
        </div>
      </div>

      {/* Cross-shop / direct teaser → full page */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-rule-2 bg-paper-2/60">
        <div className="text-[11px] text-ink-3">
          {Number(myCommission.total) > Number(myMonth.share) ? (
            <>
              Month so far: <Money value={myCommission.total} className="text-ink font-medium" />
              {myCommission.shopCount > 1 && <> across {myCommission.shopCount} shops</>}
              {Number(myCommission.online) > 0 && <> · online</>}
              {Number(myCommission.crossSale) > 0 && <> · cross-sales</>}
              {Number(myCommission.specialPayable) + Number(myCommission.specialPending) > 0 && <> · special models</>}
            </>
          ) : (
            <>Sold at another shop? Online? Special models? It all counts.</>
          )}
        </div>
        <Link to="/my-commission" className="text-xs text-accent hover:underline whitespace-nowrap">
          My commission →
        </Link>
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  hintTone,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  hintTone?: 'positive' | 'warning' | 'negative' | 'neutral';
}): JSX.Element {
  const hintColor: Record<NonNullable<typeof hintTone>, string> = {
    positive: 'text-positive',
    warning: 'text-warning',
    negative: 'text-negative',
    neutral: 'text-ink-3',
  };
  return (
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div className="text-2xl text-ink mt-1 font-medium">{value}</div>
      {hint != null && (
        <div className={cn('text-xs mt-1', hintColor[hintTone ?? 'neutral'])}>{hint}</div>
      )}
    </div>
  );
}
